import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ArrowLeft, Building2, Edit3, Trash2, Search, X, Gauge, Check,
  FileText, Eye,
} from 'lucide-react';

function ExNewBadge({ status }) {
  return status === 'existing'
    ? <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 h-4 px-1.5 font-semibold">Existing</Badge>
    : <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200 h-4 px-1.5 font-semibold">New</Badge>;
}

function ApprovalBadge({ status }) {
  return status === 'approved'
    ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 h-4 px-1.5 font-semibold gap-0.5"><Check className="h-2.5 w-2.5" />Approved</Badge>
    : <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 h-4 px-1.5 font-semibold">Review</Badge>;
}

export default function PropertyDetailsPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [prop, setProp] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('utility');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const loadData = useCallback(async () => {
    if (!accountId || !propertyId) return;
    setLoading(true);
    try {
      const [acctRes, meterRes] = await Promise.all([
        api.get(`/bill2rollin/properties/${propertyId}/accounts`, { params: { account_id: accountId } }),
        api.get(`/bill2rollin/properties/${propertyId}/meters`, { params: { account_id: accountId } }),
      ]);
      setProp(acctRes.data.property);
      setAccounts(acctRes.data.accounts || []);
      setMeters(meterRes.data.meters || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [accountId, propertyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const utilityAccounts = accounts.filter(a => a.unit_number === 'N/A' || !a.unit_number);
  const unitAccounts = accounts.filter(a => a.unit_number && a.unit_number !== 'N/A');

  const filterList = (list) => {
    let f = list;
    if (filterStatus) f = f.filter(a => a.mysql_status === filterStatus);
    if (search) {
      const s = search.toLowerCase();
      f = f.filter(a => (a.account_number || '').toLowerCase().includes(s) || (a.vendor || '').toLowerCase().includes(s) || (a.service_address || '').toLowerCase().includes(s));
    }
    return f;
  };

  const filterMeters = () => {
    let f = meters;
    if (filterStatus) f = f.filter(m => m.mysql_status === filterStatus);
    if (search) {
      const s = search.toLowerCase();
      f = f.filter(m => (m.meter_number || '').toLowerCase().includes(s) || (m.account_number || '').toLowerCase().includes(s));
    }
    return f;
  };

  const deleteAccount = async (ref) => {
    if (!window.confirm('Delete this account?')) return;
    try { await api.delete(`/bill2rollin/accounts/${ref}?account_id=${accountId}`); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };

  const deleteMeter = async (id) => {
    if (!window.confirm('Delete this meter?')) return;
    try { await api.delete(`/bill2rollin/meters/${id}?account_id=${accountId}`); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };

  const approveAccount = async (ref, status) => {
    try { await api.patch(`/bill2rollin/accounts/${ref}/approve?account_id=${accountId}&status=${status}`); toast.success(status === 'approved' ? 'Approved' : 'Set to Review'); loadData(); } catch { toast.error('Failed'); }
  };

  const bulkApprove = async (status = 'approved') => {
    if (selected.size === 0) { toast.error('Select accounts first'); return; }
    try {
      await api.post(`/bill2rollin/accounts/bulk-approve?account_id=${accountId}`, { account_id_refs: [...selected], status });
      toast.success(`${selected.size} accounts ${status}`);
      setSelected(new Set());
      loadData();
    } catch { toast.error('Bulk approve failed'); }
  };

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const currentAcctList = tab === 'utility' ? filterList(utilityAccounts) : tab === 'unit' ? filterList(unitAccounts) : [];
  const selectAll = () => {
    if (selected.size === currentAcctList.length) setSelected(new Set());
    else setSelected(new Set(currentAcctList.map(a => a.account_id_ref)));
  };

  const currentList = tab === 'meters' ? filterMeters() : currentAcctList;

  if (!accountId) return <MainLayout><div className="p-8 text-center text-sm text-muted-foreground">Select an organization.</div></MainLayout>;

  return (
    <MainLayout wide>
      <div className="space-y-4 p-4 md:p-6" data-testid="property-details-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/ubm/bill2rollin')} data-testid="b2r-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Roll In
            </Button>
            {prop && (
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-violet-600" />
                <div>
                  <h1 className="text-lg font-bold flex items-center gap-2" data-testid="prop-detail-title">
                    {prop.name}
                    <ExNewBadge status={prop.mysql_status || 'new'} />
                    <ApprovalBadge status={prop.approval_status || 'review'} />
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {[prop.address, prop.city, prop.state, prop.zip].filter(Boolean).join(', ')}
                    {prop.ein && <span className="ml-2 font-mono">EIN: {prop.ein}</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
          {prop && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/ubm/bill2rollin/edit-property/${propertyId}`)} data-testid="prop-edit-btn">
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Property
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        {prop && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { label: 'Common Area', value: utilityAccounts.length, sub: `${utilityAccounts.filter(a => a.mysql_status === 'existing').length} existing`, color: 'text-blue-700' },
              { label: 'Unit Accounts', value: unitAccounts.length, sub: `${unitAccounts.filter(a => a.mysql_status === 'existing').length} existing`, color: 'text-indigo-700' },
              { label: 'Meters', value: meters.length, sub: `${meters.filter(m => m.mysql_status === 'existing').length} existing`, color: 'text-violet-700' },
              { label: 'New Records', value: accounts.filter(a => a.mysql_status === 'new').length + meters.filter(m => m.mysql_status === 'new').length, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
              { label: 'Existing Records', value: accounts.filter(a => a.mysql_status === 'existing').length + meters.filter(m => m.mysql_status === 'existing').length, color: 'text-sky-700', bg: 'bg-sky-50 border-sky-200' },
            ].map((k, i) => (
              <div key={i} className={`border rounded-lg p-3 text-center ${k.bg || 'bg-card'}`}>
                <p className={`text-lg font-bold ${k.color}`}>{k.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{k.label}</p>
                {k.sub && <p className="text-[9px] text-muted-foreground">{k.sub}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[
            { id: 'utility', label: 'Utility Accounts (Common Area)', count: utilityAccounts.length },
            { id: 'unit', label: 'Unit Accounts', count: unitAccounts.length },
            { id: 'meters', label: 'Meters', count: meters.length },
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setFilterStatus(''); setSearch(''); setSelected(new Set()); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`prop-tab-${t.id}`}>
              {t.label} <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1.5">{t.count}</Badge>
            </button>
          ))}
        </div>

        {/* Filters + Bulk Actions */}
        <div className="border rounded-lg p-3 bg-card">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="existing">Existing</SelectItem></SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="h-8 w-44 text-xs pl-6" />
            </div>
            {(filterStatus || search) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setSearch(''); }}><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
            )}
            {tab !== 'meters' && selected.size > 0 && (
              <div className="ml-4 flex gap-1">
                <Button size="sm" className="h-7 text-[10px] gap-1" onClick={() => bulkApprove('approved')} data-testid="bulk-approve-btn">
                  <Check className="h-3 w-3" /> Approve ({selected.size})
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => bulkApprove('review')}>Set Review</Button>
              </div>
            )}
            <span className="ml-auto text-xs text-muted-foreground">{currentList.length} records</span>
          </div>
        </div>

        {/* Tables */}
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            {(tab === 'utility' || tab === 'unit') && (
              <Table className="w-full min-w-[1200px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-[30px]"><Checkbox checked={selected.size === currentAcctList.length && currentAcctList.length > 0} onCheckedChange={selectAll} className="h-3.5 w-3.5" /></TableHead>
                    <TableHead className="text-xs font-semibold w-[40px]">#</TableHead>
                    <TableHead className="text-xs font-semibold w-[130px]">Account #</TableHead>
                    <TableHead className="text-xs font-semibold w-[120px]">Vendor</TableHead>
                    {tab === 'unit' && <TableHead className="text-xs font-semibold w-[70px]">Unit</TableHead>}
                    <TableHead className="text-xs font-semibold w-[200px]">Service Address</TableHead>
                    <TableHead className="text-xs font-semibold w-[50px]">State</TableHead>
                    <TableHead className="text-xs font-semibold w-[60px]">Zip</TableHead>
                    <TableHead className="text-xs font-semibold w-[80px]">Utility</TableHead>
                    <TableHead className="text-xs font-semibold w-[70px]">Status</TableHead>
                    <TableHead className="text-xs font-semibold w-[70px]">Approval</TableHead>
                    <TableHead className="text-xs font-semibold w-[60px] text-center">Meters</TableHead>
                    <TableHead className="text-xs font-semibold w-[30px]">Pg</TableHead>
                    <TableHead className="text-xs font-semibold w-[80px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentAcctList.length === 0 ? (
                    <TableRow><TableCell colSpan={tab === 'unit' ? 14 : 13} className="text-center py-12 text-muted-foreground">No {tab === 'utility' ? 'common area' : 'unit'} accounts found.</TableCell></TableRow>
                  ) : currentAcctList.map((a, i) => (
                    <TableRow key={a.account_id_ref} className="hover:bg-muted/20" data-testid={`prop-acct-row-${i}`}>
                      <TableCell><Checkbox checked={selected.has(a.account_id_ref)} onCheckedChange={() => toggleSelect(a.account_id_ref)} className="h-3.5 w-3.5" /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-xs font-mono font-medium">{a.account_number}</TableCell>
                      <TableCell className="text-xs truncate" title={a.vendor}>{a.vendor || '\u2014'}</TableCell>
                      {tab === 'unit' && <TableCell className="text-xs font-mono">{a.unit_number}</TableCell>}
                      <TableCell className="text-xs truncate" title={a.service_address}>{a.service_address || '\u2014'}</TableCell>
                      <TableCell className="text-xs">{a.state || '\u2014'}</TableCell>
                      <TableCell className="text-xs">{a.zip || '\u2014'}</TableCell>
                      <TableCell className="text-xs"><Badge variant="outline" className="text-[9px] h-4 px-1">{a.utility_type || 'other'}</Badge></TableCell>
                      <TableCell><ExNewBadge status={a.mysql_status || 'new'} /></TableCell>
                      <TableCell>
                        <button onClick={() => approveAccount(a.account_id_ref, a.approval_status === 'approved' ? 'review' : 'approved')} className="cursor-pointer">
                          <ApprovalBadge status={a.approval_status || 'review'} />
                        </button>
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 h-4 px-1.5"><Gauge className="h-2.5 w-2.5 mr-0.5" />{a.meters_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-violet-600">{a.page_ref || '\u2014'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigate(`/ubm/bill2rollin/edit/${a.account_id_ref}?from=property&propertyId=${propertyId}`)} data-testid={`prop-edit-${i}`}>
                            <Edit3 className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteAccount(a.account_id_ref)} data-testid={`prop-del-${i}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 'meters' && (
              <Table className="w-full min-w-[900px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="text-xs font-semibold w-[40px]">#</TableHead>
                    <TableHead className="text-xs font-semibold w-[150px]">Meter Number</TableHead>
                    <TableHead className="text-xs font-semibold w-[130px]">Meter Identifier</TableHead>
                    <TableHead className="text-xs font-semibold w-[130px]">Account #</TableHead>
                    <TableHead className="text-xs font-semibold w-[120px]">Vendor</TableHead>
                    <TableHead className="text-xs font-semibold w-[80px]">Utility</TableHead>
                    <TableHead className="text-xs font-semibold w-[70px]">Status</TableHead>
                    <TableHead className="text-xs font-semibold w-[30px]">Pg</TableHead>
                    <TableHead className="text-xs font-semibold w-[60px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentList.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No meters found.</TableCell></TableRow>
                  ) : currentList.map((m, i) => (
                    <TableRow key={m.meter_id} className="hover:bg-muted/20" data-testid={`prop-meter-row-${i}`}>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-xs font-mono font-medium">{m.meter_number}</TableCell>
                      <TableCell className="text-xs font-mono">{m.meter_identifier || '\u2014'}</TableCell>
                      <TableCell className="text-xs font-mono">{m.account_number || '\u2014'}</TableCell>
                      <TableCell className="text-xs truncate">{m.vendor || '\u2014'}</TableCell>
                      <TableCell className="text-xs"><Badge variant="outline" className="text-[9px] h-4 px-1">{m.utility_type || 'other'}</Badge></TableCell>
                      <TableCell><ExNewBadge status={m.mysql_status || 'new'} /></TableCell>
                      <TableCell className="text-xs font-mono text-violet-600">{m.page_ref || '\u2014'}</TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteMeter(m.meter_id)} data-testid={`prop-meter-del-${i}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
